import "./App.css";

import React, { useState, useEffect } from "react";

import Typography from "@mui/material/Typography";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningIcon from "@mui/icons-material/Warning";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ErrorIcon from "@mui/icons-material/Error";

import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { MuiChipsInput } from "mui-chips-input";
import * as lodash from "lodash";

import * as riki from "jsriki";

import { activeLabelsState, projectPickerState } from "./JotaiAtoms";
import { reloadState } from "./JotaiAtoms";

import { useAtom, useAtomValue, useSetAtom } from "jotai";

import log from "./logger";

import * as u from "./utility";

import { DEFAULT_FONT_SIZE } from "./constants";
import {
  useHttpPostAndProject,
  useHookUrl,
  projectSettingsAtom,
  httpPostAtom,
  ProjectSettings,
} from "./hooks";
import { READ_ONLY_CONF } from "./constants";
import Divider from "@mui/material/Divider";

import { Theme, useTheme } from "@mui/material/styles";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { getCurrentUserEmail } from "./Auth";

import { hsvaToHex, hexToHsva, HsvaColor } from "@uiw/react-color";

type UserRole = {
  project: string;
  role: RoleName;
  email: string;
  created_at?: string;
};

function DangerousZone({ roles }: { roles: UserRole[] }) {
  const [confirmation, setConfirmation] = useState("");
  const [statement, setStatement] = useState("");
  const [httpPost, project] = useHttpPostAndProject();
  const [currentUser, setCurrentUser] = useState<string>();

  useEffect(() => {
    getCurrentUserEmail()
      .then((email: string) => setCurrentUser(email))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (project) {
      setStatement(`permanently delete ${project}`);
    } else {
      setStatement("");
    }
  }, [project]);

  const isCurrentUserOwner = () => {
    const owner = roles.find((e) => e.role === "owner");
    if (!owner) {
      return false;
    }

    return owner.email === currentUser;
  };

  const handleDelete = () => {
    const request = async () => {
      try {
        await httpPost("admin", { action: "revoke" });
        window.location.href = u.getHomeUrl();
      } catch (err) {}
    };

    if (statement && confirmation === statement) {
      setConfirmation("");
      request();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmation(e.target.value);
  };

  return (
    <>
      {isCurrentUserOwner() && (
        <Box
          border={1}
          sx={{ p: 2, mt: 2, backgroundColor: "#ffefef" }}
          color="red"
          borderRadius={3}
        >
          <Typography color="red" fontWeight={"bold"} fontSize={14}>
            Dangerous stuff. Tread carefully.
          </Typography>
          <Stack direction={"row"} gap={1} sx={{ mt: 2 }}>
            <TextField
              label={statement}
              value={confirmation}
              onChange={handleChange}
            />
            <Button
              onClick={handleDelete}
              disabled={confirmation !== statement}
            >
              Delete project
            </Button>
          </Stack>
        </Box>
      )}
    </>
  );
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function getStyles(name: string, personName: string[], theme: Theme) {
  return {
    fontWeight: personName.includes(name)
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
  };
}

export default function SelectDropdown({
  current,
  options,
  label,
  fontSize,
  onChange,
}: {
  current: string;
  options: string[];
  label: string;
  fontSize?: number | string;
  onChange?: (value: string) => void;
}) {
  const theme = useTheme();
  const [option, setOption] = React.useState<string>(current);

  const handleChange = (event: SelectChangeEvent<string>) => {
    setOption(event.target.value);
    if (onChange) {
      onChange(event.target.value);
    }
  };

  return (
    <div>
      <FormControl sx={{ m: 1, width: 150 }}>
        {/* <InputLabel id="demo-multiple-name-label">{label}</InputLabel> */}
        <Select
          labelId="demo-multiple-name-label"
          value={option}
          onChange={handleChange}
          // input={<OutlinedInput label={label} />}
          MenuProps={MenuProps}
          variant="standard"
          sx={{ fontSize }}
        >
          {options.map((option) => (
            <MenuItem key={option} value={option} style={{ fontSize }}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
}

const ROLES = ["admin", "viewer", "owner"] as const;
type RoleName = (typeof ROLES)[number];

function TeamMembers({ roles }: { roles: UserRole[] }) {
  const [chips, setChips] = useState<string[]>();
  const reloadData = useAtomValue(reloadState);
  const [httpPost, project] = useHttpPostAndProject();
  const [rolesCopy, setRoles] = useState<UserRole[]>([]);

  useEffect(() => {
    const userEmails = roles
      .filter((e) => e.role !== "owner")
      .map((e) => e.email);
    setChips(userEmails);
    setRoles([...roles]);
  }, [roles]);

  const grantRequest = async (action: string, email: string, role?: string) => {
    try {
      await httpPost("admin", { action: action, email: email, role });
    } catch (err) {}
  };

  const handleChange = (newChips: string[]) => {
    for (let email of newChips) {
      if (!chips || !chips.includes(email)) {
        grantRequest("grant", email)
          .then(() => {
            setRoles((prev: UserRole[]) => {
              const existing = prev.find((e) => e.email === email);
              if (existing) {
                existing.role = "admin";
                return [...roles];
              } else {
                return [...roles, { project, email, role: "admin" }];
              }
            });
          })
          .catch(() => {});
      }
    }

    if (chips) {
      for (let email of chips) {
        if (!newChips.includes(email)) {
          grantRequest("revoke", email)
            .then(() => {
              setRoles((prev: UserRole[]) => {
                const f = prev.findIndex((e) => e.email === email);
                if (f !== -1) {
                  return [...prev.slice(0, f), ...prev.slice(f + 1)];
                }
                return prev;
              });
            })
            .catch(() => {});
        }
      }
    }
    setChips(newChips);
  };

  const handleChangeRole = (userEmail: string, role: RoleName) => {
    grantRequest("grant", userEmail, role)
      .then(() => {
        setRoles((prev: UserRole[]) => {
          const f = prev.findIndex((e) => e.email === userEmail);
          if (f !== -1) {
            prev[f].role = role;
            return [...prev];
          }
          return prev;
        });
      })
      .catch(() => {});
  };

  return (
    <Box border={1} sx={{ p: 2 }} borderRadius={3}>
      <Typography
        fontWeight={"bold"}
        sx={{ mb: 2 }}
        fontSize={DEFAULT_FONT_SIZE}
      >
        Manage your team, invite or remove people. Enter an email.
      </Typography>
      <MuiChipsInput value={chips || []} onChange={handleChange} />

      {rolesCopy.length > 0 && <Divider sx={{ mt: 3, mb: 1 }} />}
      {rolesCopy.map((role) => (
        <Stack direction={"row"} gap={2} alignItems={"center"} key={role.email}>
          <Typography
            fontWeight={"bold"}
            fontSize={"x-small"}
            sx={{ width: 200 }}
          >
            {role.email}
          </Typography>
          {role.role !== "owner" && (
            <SelectDropdown
              current={role.role}
              options={[...ROLES].filter((e) => e !== "owner")}
              label="Role"
              fontSize={"x-small"}
              onChange={(newRole: string) =>
                handleChangeRole(role.email, newRole as RoleName)
              }
            />
          )}
          {role.role === "owner" && (
            <Typography fontSize={"x-small"} sx={{ ml: 1 }}>
              {role.role}
            </Typography>
          )}
        </Stack>
      ))}
    </Box>
  );
}

type KeyValueConfigProps = {
  items: {
    name: string;
    value: string;
    editable?: boolean;
    warn?: string;
    health?: ConfigHealth;
    replaceable?: boolean;
  }[];
  onCreate: (name: string, value: string) => void;
  onEdit: (name: string, value: string, index: number) => void;
  onDelete?: (index: number) => void;
};

function KeyValueConfig({
  items,
  onCreate,
  onEdit,
  onDelete,
}: KeyValueConfigProps) {
  const [generation, setGeneration] = useState(0);

  const handleDelete = (index: number) => {
    setGeneration((prev) => prev + 1);
    if (onDelete) {
      onDelete(index);
    }
  };

  const isDuplicateOrEmpty = (key: string, index?: number) => {
    if (!key) {
      return true;
    }

    for (let i = 0; i < items.length; ++i) {
      if (i !== index && key === items[i].name) {
        return true;
      }
    }

    return false;
  };

  const handleEdit = (key: string, value: string, index: number) => {
    if (isDuplicateOrEmpty(key, index)) {
      return false;
    }
    const allowed = onEdit(key, value, index);
    if (allowed === undefined || allowed) {
      return true;
    }

    return false;
  };

  const handleCreate = (key: string, value: string) => {
    if (isDuplicateOrEmpty(key)) {
      return false;
    }
    onCreate(key, value);
    return true;
  };

  return (
    <TableContainer component={Box}>
      <Table
        aria-label="simple table"
        size="small"
        width="100%"
        sx={{ tableLayout: "fixed", wordWrap: "break-word" }}
      >
        <TableBody>
          {items &&
            items.map((row, index) => (
              <ConfigLine
                key={`${generation}:${index}`}
                initKey={row.name}
                initValue={row.value}
                editable={row.editable === undefined || row.editable}
                onEdit={(k, v) => handleEdit(k, v, index)}
                onDelete={onDelete ? () => handleDelete(index) : undefined}
                warn={row.warn}
                health={row.health}
                replaceable={row.replaceable}
              />
            ))}
          {items && (
            <ConfigLine
              key={items.length}
              initKey=""
              initValue=""
              editable={true}
              onEdit={handleCreate}
            />
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ProjectConfig() {
  type ConfigData = {
    name: string;
    value: string;
    editable?: boolean;
    warn?: string;
    replaceable?: boolean;
    health?: ConfigHealth;
  };

  const [config, setConfig] = useState<ConfigData[]>([]);
  const [httpPost] = useHttpPostAndProject();
  const settings = useAtomValue(projectSettingsAtom);

  // const pendingUpdate = (key: string, value: string) => {
  //   setTimeout(() => {
  //     getData();
  //   }, 2000);
  // };

  const handleEdit = async (key: string, value: string, index?: number) => {
    const request = async (
      key: string,
      value: string
    ): Promise<undefined | { data: any }> => {
      try {
        return await httpPost("admin", {
          action: "config",
          key: key,
          value: value,
        });
      } catch (err) {
        return undefined;
      } finally {
      }
    };

    let ok: undefined | { data: any } = undefined;
    if (index !== undefined) {
      if (config[index].name !== key || config[index].value !== value) {
        let newConfig = [...config];
        newConfig[index] = {
          name: key,
          value: value,
        };
        setConfig(newConfig);
        ok = await request(key, value);
        if (!ok) {
          setConfig((prev: ConfigData[]) => {
            const found = prev.findIndex(
              (e) => e.name == key && e.value === value
            );
            if (found !== -1) {
              const next = [...prev];
              prev[found].warn = "Something is wrong, perhaps a bad name.";
              return next;
            } else {
              return prev;
            }
          });
        }
        if (ok) {
          // rename or delete a previously existing config
          if (key !== config[index].name) {
            ok = await request(config[index].name, "");
          }
        }
      }
    } else {
      // create a new config
      ok = await request(key, value);
    }

    if (ok) {
      // pendingUpdate(key, value);
      mergeConfigs(ok.data.config);
      healthCheck();
    }

    return ok;
  };

  const handleNewConfig = async (key: string, value: string) => {
    setConfig([
      ...config,
      {
        name: key,
        value: value,
      },
    ]);
    if (key && value) {
      const ok = await handleEdit(key, value);
      if (!ok) {
        setConfig((prev: ConfigData[]) => {
          const found = prev.findIndex(
            (e) => e.name == key && e.value === value
          );
          if (found !== -1) {
            const next = [...prev];
            prev[found].warn = "Something is wrong, perhaps a bad name.";
            return next;
          } else {
            return prev;
          }
        });
      }
    }
  };

  const handleDelete = (index: number) => {
    const request = async () => {
      try {
        await httpPost("admin", {
          action: "config",
          key: config[index].name,
          value: "",
        });
      } catch (err) {
      } finally {
      }
    };
    request();

    let newConfig = config.slice(0, index).concat(config.slice(index + 1));
    setConfig(newConfig);
  };

  const mergeConfigs = (inputConfig: any) => {
    let conf = [];
    for (let [k, v] of Object.entries(inputConfig)) {
      if (k !== "SK" && k !== "PK" && k !== "original_owner") {
        conf.push({
          name: k,
          value: v as string,
          editable: !READ_ONLY_CONF.includes(k),
          replaceable: k.includes("secret") || k.includes("token"),
        });
      }
    }
    const order = [...READ_ONLY_CONF, "slack", "github", "gitlab"];
    let reordered: ConfigData[] = [];
    let visited = new Set();
    for (let o of order) {
      for (let c of conf) {
        if (c.name.startsWith(o)) {
          reordered.push(c);
          visited.add(c.name);
        }
      }
    }
    for (let c of conf) {
      if (!visited.has(c.name)) {
        reordered.push(c);
      }
    }

    // merge configs
    setConfig((prev) => {
      if (prev.length === 0) {
        if (reordered.length === 0) {
          return prev;
        } else {
          return reordered;
        }
      } else {
        const out: ConfigData[] = [];
        prev.forEach((e) => {
          const found = reordered.find((x) => e.name === x.name);
          if (found) {
            out.push({ ...found, health: e.health });
          } else if (e.warn) {
            out.push(e);
          }
        });
        reordered.forEach((e) => {
          const found = out.find((x) => e.name === x.name);
          if (!found) {
            out.push(e);
          }
        });
        return out;
      }
    });
  };

  const updateHealthStatus = (healthStatus: ConfigHealth[]) => {
    setConfig((prev: ConfigData[]) => {
      const newConfig = [...prev];
      newConfig.forEach((e) => {
        e.health = undefined;
      });

      for (const health of healthStatus) {
        const configName = health.name;
        const f = newConfig.find((e) => e.name == configName);
        if (f) {
          f.health = health;
        }
      }

      return newConfig;
    });
  };

  const healthCheck = () => {
    const request = async () => {
      try {
        const response = await httpPost("admin", { action: "healthcheck" });
        updateHealthStatus(response.data);
      } catch (err) {
        log.debug(err);
      } finally {
      }
    };
    request();
  };

  useEffect(() => {
    if (settings) {
      mergeConfigs(settings.config);
      healthCheck();
    }
  }, [settings]);

  return (
    <Stack direction={"column"}>
      <Typography fontWeight={"bold"} sx={{ m: 2 }} fontSize={14}>
        Project key-value settings:
      </Typography>
      <KeyValueConfig
        items={config}
        onCreate={handleNewConfig}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </Stack>
  );
}

function Workflow() {
  const [workflow, setWorkflow] = useState<string>();
  const [error, setError] = useState(false);
  const activeLabels = useAtomValue(activeLabelsState);
  const [dirty, setDirty] = useState(false);
  const [focused, setFocused] = useState(false);
  const settings = useAtomValue(projectSettingsAtom);
  const [httpPost] = useHttpPostAndProject();

  useEffect(() => {
    if (settings) {
      setWorkflow(JSON.stringify(settings.workflow, null, 2));
      setError(false);
    }
  }, [settings]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWorkflow(e.target.value || " ");
    setDirty(true);
  };

  const handleSave = () => {
    const request = async (workflow: any) => {
      try {
        await httpPost("admin", {
          action: "setworkflow",
          workflow: workflow,
        });
      } catch (err) {}
    };

    try {
      let parsed: { states: { [key: string]: string[] } } =
        JSON.parse('{"states":{}}');
      if (workflow && workflow.trim()) {
        parsed = JSON.parse(workflow);
      }

      let display: { [key: string]: string } = {};
      if (activeLabels) {
        for (let [k, v] of Object.entries(activeLabels)) {
          const d = v.display;
          if (typeof d === "string") {
            display[d] = k;
          }
        }
      }

      let fixedWorkflow: { states: { [key: string]: string[] } } = {
        states: {},
      };
      for (let [k, v] of Object.entries(parsed.states)) {
        let fixed: string[] = [];
        for (let label of v) {
          if (display[label] && (!activeLabels || !activeLabels[label])) {
            fixed.push(display[label]);
          } else {
            fixed.push(label);
          }
        }
        fixedWorkflow.states[k] = fixed;
      }

      setWorkflow(JSON.stringify(fixedWorkflow, null, 2));
      request(fixedWorkflow);
      setError(false);
      setDirty(false);
    } catch (e) {
      setError(true);
    }
  };

  const handleCancel = () => {
    if (settings) {
      setWorkflow(JSON.stringify(settings.workflow, null, 2));
      setDirty(false);
    }
  };

  return (
    <Box border={0} sx={{ m: 0, p: 2 }}>
      <TextField
        error={error}
        fullWidth
        size="small"
        value={workflow || " "}
        multiline
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        variant="outlined"
        label="Default Workflow"
        inputProps={{
          style: {
            fontSize: 12,
            fontFamily: "monospace",
            lineHeight: 1.5,
            color: focused ? "black" : "#707070",
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Stack direction={"row"}>
                <Button onClick={handleSave} disabled={!dirty}>
                  Save
                </Button>
                <Button onClick={handleCancel} disabled={!dirty}>
                  Cancel
                </Button>
              </Stack>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
}

export function Settings() {
  const [denied, setDenied] = useState<"allowed" | "denied" | "unknown">(
    "unknown"
  );
  const hookUrl = useHookUrl();
  const [roles, setRoles] = useState<UserRole[]>([]);
  const settings = useAtomValue(projectSettingsAtom);

  useEffect(() => {
    if (settings) {
      setDenied("allowed");

      let roles = settings.roles;
      const owner = roles.findIndex((e: UserRole) => e.role === "owner");
      if (owner !== -1) {
        roles = [
          roles[owner],
          ...roles.slice(0, owner),
          ...roles.slice(owner + 1),
        ];
      }
      setRoles(roles);
    } else {
      setDenied("denied");
    }
  }, [settings]);

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(hookUrl);
  };

  return (
    <>
      <ProjectSettingsData />
      {denied === "allowed" && (
        <Stack>
          <Stack maxWidth={750} direction="column">
            <Box border={1} sx={{ mt: 2, mb: 2, p: 2 }} borderRadius={3}>
              <Typography fontSize={9}>Your webhook URL:</Typography>
              <Stack direction={"row"} alignItems={"center"}>
                <Typography>{hookUrl}</Typography>
                <IconButton size="small" sx={{ pl: 1 }} onClick={handleCopyUrl}>
                  <ContentCopyIcon fontSize="inherit" />
                </IconButton>
              </Stack>
            </Box>
            <Stack border={1} borderRadius={3} gap={5} sx={{ p: 2 }}>
              <ProjectConfig />
              <Workflow />
              <UserQueries />
            </Stack>
            <Box sx={{ m: 1 }}></Box>
            <TeamMembers roles={roles} />
            <DangerousZone roles={roles} />
          </Stack>
        </Stack>
      )}
    </>
  );
}

type ConfigUsage = {
  total: number;
  err?: number;
  ok?: number;
  last_ok?: string;
  last_err?: string;
};

type ResourceUsage = {
  last_seen: string;
  num_seen: number;
  name: string;
  disabled?: boolean;
};

type ConfigHealth = {
  status: "ok" | "err";
  name: string;
  usage?: {
    [key: string]: ConfigUsage;
  };
  resources?: {
    [key: string]: ResourceUsage;
  };
};

type ConfigLineProps = {
  initKey: string;
  initValue: string;
  onEdit: (name: string, value: string) => boolean;
  editable: boolean;
  onDelete?: () => void;
  warn?: string;
  health?: ConfigHealth;
  replaceable?: boolean;
};

const createHealthReport = (health: ConfigHealth) => {
  type Report = {
    errorRate?: number;
    successRate?: number;
    url: string;
    lastSuccess?: string;
    lastError?: string;
    errorCodes: string[];
  };

  type ResourceReport = {
    name: string;
    ts: string;
    disabled?: boolean;
  };

  let report: Report[] = [];

  const successTimestamps = Object.values(health.usage!)
    .map((usage) => usage.last_ok)
    .filter((e) => e !== undefined)
    .sort()
    .reverse() as string[];
  const errorTimestamps = Object.values(health.usage!)
    .map((usage) => usage.last_err)
    .filter((e) => e !== undefined)
    .sort()
    .reverse() as string[];

  Object.entries(health.usage!)
    .sort((a, b) => b[1].total - a[1].total)
    .forEach(([url, usage]) => {
      const errorRate = usage.err
        ? Math.round((10000 * usage.err) / usage.total)
        : undefined;

      const successRate = usage.ok
        ? Math.round((10000 * usage.ok) / usage.total)
        : undefined;

      const errorCodes = Object.entries(usage)
        .filter(([key, value]) => key.startsWith("status_"))
        .sort((a, b) => (b[1] as number) - (a[1] as number))
        .map((e) => e[0].slice(7))
        .filter((e) => e !== "200");

      const lastSuccess = usage.last_ok;
      const lastError = usage.last_err;
      report.push({
        errorRate,
        successRate,
        url,
        lastSuccess,
        lastError,
        errorCodes,
      });
    });

  const saturate = (
    base_color: string,
    all_timestamps: string[],
    ts?: string
  ) => {
    if (!ts) {
      return base_color;
    }

    const hsva = hexToHsva(base_color);
    const rank =
      (all_timestamps.length - all_timestamps.findIndex((e) => e === ts)) /
      all_timestamps.length;
    hsva.s *= rank;
    return hsvaToHex(hsva);
  };

  const green = (last_ok: string | undefined, timestamps: string[]) => {
    // const lightgreen = '#90EE90';
    const green = "#00FF00";
    return saturate(green, timestamps, last_ok);
  };

  const red = (last_err?: string) => {
    // const red = '#FF0000';
    const tomato = "#FF6347";
    return saturate(tomato, errorTimestamps, last_err);
  };

  const blue = (last_ok: string | undefined, timestamps: string[]) => {
    return saturate("#87CEFA", timestamps, last_ok);
  };

  const successRate = (e: Report) => {
    return (
      <>
        {e.successRate !== undefined && e.successRate > 0 && (
          <span
            style={{
              color: green(e.lastSuccess, successTimestamps),
              fontWeight: "bold",
            }}
          >{`${e.successRate / 100}% `}</span>
        )}
      </>
    );
  };

  const errorRate = (e: Report) => {
    return (
      <>
        {e.errorRate !== undefined && e.errorRate > 0 && (
          <span style={{ color: red(e.lastError), fontWeight: "bold" }}>{`${
            e.errorRate / 100
          }% `}</span>
        )}
      </>
    );
  };

  let resources: ResourceReport[] = [];
  let resourceTimestamps: string[] = [];

  if (health.resources) {
    resourceTimestamps = Object.values(health.resources)
      .map((usage) => usage.last_seen)
      .sort()
      .reverse();
    resources = Object.values(health.resources)
      .map((e) => {
        return { ...e, num_seen: e.disabled ? -1 : e.num_seen };
      })
      .sort((a, b) => b.num_seen - a.num_seen)
      .map((e) => {
        return { name: e.name, ts: e.last_seen, disabled: e.disabled };
      });
  }

  return (
    <>
      {report.map((e) => {
        return (
          <div key={e.url}>
            {e.lastError && e.lastSuccess && e.lastError > e.lastSuccess ? (
              <>
                {errorRate(e)}
                {successRate(e)}
              </>
            ) : (
              <>
                {successRate(e)}
                {errorRate(e)}
              </>
            )}
            {e.url}
            {e.errorCodes.map((code) => {
              return (
                <span key={code} style={{ color: "tomato" }}>
                  {` ${code}`}
                </span>
              );
            })}
          </div>
        );
      })}
      {resources.length > 0 && (
        <>
          <Divider sx={{ borderColor: "silver", m: 1 }} />
          {resources.map((e) => {
            return (
              <Box
                component={"span"}
                sx={{
                  color: !e.disabled
                    ? blue(e.ts, resourceTimestamps)
                    : "#a0a0a0",
                  m: 0.2,
                }}
              >
                {`${e.name} `}
              </Box>
            );
          })}
        </>
      )}
    </>
  );
};

function HealthStatusIcon({ health }: { health?: ConfigHealth }) {
  return (
    <>
      {health?.status === "ok" && (
        <>
          {health.usage && (
            <Tooltip title={createHealthReport(health)}>
              <CheckBoxIcon sx={{ fontSize: 17, color: "green" }} />
            </Tooltip>
          )}
          {!health.usage && (
            <CheckBoxIcon sx={{ fontSize: 17, color: "green" }} />
          )}
        </>
      )}

      {health?.status === "err" && (
        <>
          {health.usage && (
            <Tooltip title={createHealthReport(health)}>
              <ErrorIcon sx={{ fontSize: 17, color: "red" }} />
            </Tooltip>
          )}
          {!health.usage && <ErrorIcon sx={{ fontSize: 17, color: "red" }} />}
        </>
      )}
    </>
  );
}

function ConfigLine({
  initKey,
  initValue,
  onEdit,
  editable,
  onDelete,
  warn,
  health,
  replaceable,
}: ConfigLineProps) {
  const [editKey, setEditKey] = useState(false);
  const [editValue, setEditValue] = useState(false);
  const [key, setKey] = useState(initKey);
  const [value, setValue] = useState(initValue);
  const [focusValue, setFocusValue] = useState(true);

  const handleEditKey = () => {
    setEditKey(true);
    setEditValue(false);
  };

  const handleEditValue = () => {
    setEditValue(true);
    setEditKey(false);
  };

  const handleEdit = (
    e: React.KeyboardEvent<HTMLDivElement>,
    field: string
  ) => {
    if (
      e.key === "Enter" ||
      (e.key === "Tab" && !replaceable && field === "value")
    ) {
      e.preventDefault();

      if (field === "key") {
        const currentKey = (e.target as any).value;
        setKey(currentKey);

        let editAllowed = currentKey.length > 0;
        if (currentKey !== initKey) {
          editAllowed = onEdit(currentKey, value);
        }

        if (editAllowed) {
          setEditKey(false);
          setFocusValue(true);
          setEditValue(true);
        }
      } else if (field === "value") {
        const currentValue = (e.target as any).value;
        if (onEdit(key, currentValue)) {
          setValue(currentValue);
          setFocusValue(false);
          setEditValue(false);
        }
      }
    } else if (
      e.key === "Escape" ||
      (e.key === "Tab" && !replaceable && field === "value")
    ) {
      if (field === "key") {
        setKey(initKey);
        setEditKey(false);
      } else {
        setValue(initValue);
        setEditValue(false);
      }
    }
  };

  const handleBlur = (e: any, field: string) => {
    if (field === "key") {
      const current = e.target.value;
      if (current && current !== initKey) {
        if (onEdit(current, value)) {
          setKey(current);
        } else {
          setKey(initKey);
        }
      }
      setEditKey(false);
    } else if (field === "value") {
      const current = e.target.value;
      if (current && current !== initValue) {
        if (onEdit(key, current)) {
          setValue(current);
        }
      }
      setEditValue(false);
    }
  };

  return (
    <TableRow
      key={"_add_new_project_configuration_"}
      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
    >
      <TableCell
        width="35%"
        component="th"
        scope="row"
        onClick={editable ? handleEditKey : undefined}
      >
        {!editKey && (
          <Stack
            direction="row"
            gap={0.5}
            alignItems={"center"}
            // justifyContent={!initKey ? "" : "space-between"}
          >
            {warn && (
              <Tooltip title={warn}>
                <WarningIcon sx={{ fontSize: 17, color: "orange" }} />
              </Tooltip>
            )}

            <HealthStatusIcon health={health} />

            <Typography
              fontSize={12}
              color={!editable ? "grey" : undefined}
              sx={{ maxWidth: "100%" }}
            >
              {key}
            </Typography>

            {editable && (
              <IconButton size="small" onClick={handleEditKey}>
                <>
                  {(initKey || initValue) && (
                    <EditIcon sx={{ fontSize: 12, color: "silver" }} />
                  )}
                  {!initKey && !initValue && <AddIcon fontSize="inherit" />}
                </>
              </IconButton>
            )}
          </Stack>
        )}
        {editKey && (
          <TextField
            fullWidth
            autoFocus
            defaultValue={key}
            variant="standard"
            onKeyDown={(e) => {
              handleEdit(e, "key");
            }}
            onBlur={(e) => {
              handleBlur(e, "key");
            }}
            inputProps={{
              style: { fontSize: 12 },
            }}
          />
        )}
      </TableCell>
      <TableCell
        align="left"
        onClick={editable && key ? handleEditValue : undefined}
      >
        {!editValue && key && (
          <Stack
            direction="row"
            gap={0.5}
            alignItems={"center"}
            // justifyContent={"space-between"}
          >
            <Typography
              fontSize={12}
              sx={{ textWrap: "nowrap", overflow: "hidden" }}
              color={!editable ? "grey" : undefined}
            >
              {value}
            </Typography>
            {editable && (
              <IconButton size="small" onClick={handleEditValue}>
                <EditIcon sx={{ fontSize: 12, color: "silver" }} />
              </IconButton>
            )}
          </Stack>
        )}
        {editValue && (
          <TextField
            fullWidth
            autoFocus
            defaultValue={!replaceable ? value : ""}
            variant="standard"
            focused={focusValue}
            onKeyDown={(e) => {
              handleEdit(e, "value");
            }}
            onBlur={(e) => {
              handleBlur(e, "value");
            }}
            inputProps={{
              style: { fontSize: 12 },
            }}
          />
        )}
      </TableCell>
      <TableCell align="right" sx={{ width: "50px" }}>
        {onDelete && editable && (
          <IconButton onClick={onDelete} size="small">
            <DeleteIcon fontSize={"inherit"} />
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  );
}

function UserQueries() {
  const [items, setItems] = useState<
    { name: string; value: string; warn?: string }[]
  >([]);
  const [activeLabels, setActiveLabels] = useAtom(activeLabelsState);
  const [httpPost, project] = useHttpPostAndProject();
  const settings = useAtomValue(projectSettingsAtom);

  useEffect(() => {
    if (settings) {
      const sorted = settings.activelabels.queries.sort(
        (a: any, b: any) => Number(b.created) - Number(a.created)
      );
      const newItems = sorted.map((e: any) => {
        return { name: e.name, value: e.query };
      });
      setItems(newItems);
    }
  }, [settings]);

  const isShadowedByRegularLabel = (name: string) => {
    return activeLabels && activeLabels[name] && !activeLabels[name].expression
      ? true
      : false;
  };

  const getData = (project: string) => {
    if (project) {
      const request = async () => {
        try {
          const result = await httpPost("stories", {
            activelabels: true,
          });

          const sorted = result.data.activelabels.queries.sort(
            (a: any, b: any) => Number(b.created) - Number(a.created)
          );
          const newItems = sorted.map((e: any) => {
            return { name: e.name, value: e.query };
          });
          setItems(newItems);
        } catch (err) {}
      };
      request();
    }
  };

  const updateQuery = (project: string, name: string, expression: string) => {
    if (project) {
      const request = async () => {
        try {
          await httpPost("admin", {
            action: "updatequery",
            name: name,
            expression: expression,
          });

          setActiveLabels((prev) => {
            const preexisting = prev ? prev[name] : undefined;
            if (preexisting) {
              if (preexisting.expression) {
                let next = { ...prev };
                if (expression) {
                  next[name] = { ...preexisting, expression: expression };
                } else {
                  delete next[name];
                }
                return next;
              }
              return prev;
            } else {
              let next = { ...prev };
              next[name] = {
                name: name,
                display: name,
                expression: expression,
                index: prev ? Object.keys(prev).length : 0,
              };
              return next;
            }
          });
        } catch (err) {}
      };

      if (project) {
        request();
      }
    }
  };

  const handleCreate = (name: string, value: string) => {
    let newItems = [
      ...items,
      {
        name: name,
        value: value,
        warn: isShadowedByRegularLabel(name)
          ? "Shadowed by existing label with same name."
          : undefined,
      },
    ];
    setItems(newItems);
  };

  const handleEdit = (name: string, value: string, index: number) => {
    if (value && value.trim() && !riki.validate_expression(value.trim())) {
      return false;
    }

    let newItems = [...items];
    newItems[index] = {
      name: name,
      value: value,
      warn: isShadowedByRegularLabel(name)
        ? "Shadowed by existing label with same name."
        : undefined,
    };
    setItems(newItems);

    updateQuery(project, name, value);
  };

  const handleDelete = (index: number) => {
    updateQuery(project, items[index].name, "");

    let newItems = items.slice(0, index).concat(items.slice(index + 1));
    setItems(newItems);
  };

  return (
    <Stack direction={"column"}>
      <Typography fontWeight={"bold"} sx={{ m: 2 }} fontSize={14}>
        User Defined Queries:
      </Typography>
      <KeyValueConfig
        items={items}
        onCreate={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </Stack>
  );
}

function ProjectSettingsData({}) {
  const project = useAtomValue(projectPickerState);
  const reload = useAtomValue(reloadState);
  const httpPost = useAtomValue(httpPostAtom);
  const setSettings = useSetAtom(projectSettingsAtom);

  useEffect(() => {
    const request = async () => {
      try {
        const newSettings = await (
          await httpPost("admin", { action: "settings" })
        ).data;

        const dummy = {
          users: [],
          config: {},
          workflow: {},
          roles: [],
          activelabels: { channels: {}, labels: [], objects: {}, queries: [] },
        };
        setSettings((prev?: ProjectSettings) => {
          if (!prev || !lodash.isEqual(prev, newSettings)) {
            return newSettings;
          }
          return prev;
        });
      } catch (e) {}
    };
    if (project) {
      request();
    }
  }, [project, reload]);

  return null;
}
